/**
 * SliderWidget - Numeric range input widget
 *
 * Perfect for pain levels, ratings, progress, etc.
 * Voice can say "Set it to 7" to change the value.
 */

import { memo, useState } from 'react';
import type { SliderWidget as SliderWidgetType } from '@deep-agents/shared-types';
import type { WidgetComponentProps } from '../types';
import { WIDGET_PADDING } from '../constants';

interface SliderWidgetProps extends Omit<WidgetComponentProps, 'widget'> {
  widget: SliderWidgetType;
}

export const SliderWidgetComponent = memo(function SliderWidgetComponent({
  widget,
  isSelected,
  onSelect,
}: SliderWidgetProps) {
  const { props, voice } = widget;
  const { min = 0, max = 10, step = 1, defaultValue = 5, label } = props;

  // Local preview value (not persisted in builder)
  const [value, setValue] = useState(defaultValue);

  // Calculate percentage for the filled track
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div
      className={`
        relative rounded-xl transition-all duration-200 cursor-pointer
        bg-white border border-gray-200
        ${isSelected ? 'ring-2 ring-violet-500 ring-offset-2' : 'hover:border-gray-300'}
      `}
      style={{ padding: WIDGET_PADDING }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      {/* Label and value */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-700">
          {label || '🎚️ Slider'}
        </span>
        <span className="text-lg font-bold text-violet-600">
          {value}
        </span>
      </div>

      {/* Slider track */}
      <div className="relative h-2 bg-gray-200 rounded-full">
        {/* Filled portion */}
        <div
          className="absolute left-0 top-0 h-full bg-violet-500 rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        />
        {/* Thumb */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white border-2 border-violet-500 rounded-full shadow-md transition-all"
          style={{ left: `calc(${percentage}% - 10px)` }}
        />
      </div>

      {/* Min/Max labels */}
      <div className="flex justify-between mt-1.5 text-xs text-gray-400">
        <span>{min}</span>
        <span>{max}</span>
      </div>

      {/* Interactive slider (for preview in builder) */}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer nodrag"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Voice trigger hint */}
      {voice?.speakable && (
        <div className="mt-3 pt-2 border-t border-gray-100">
          <div className="flex items-center gap-1 text-[10px] text-violet-600">
            <span>🗣️</span>
            <span>"Set it to {Math.round((min + max) / 2)}"</span>
          </div>
        </div>
      )}
    </div>
  );
});
